import { create } from "zustand";
import { Profile } from "@/data/seed";
import { supabase } from "@/integrations/supabase/client";
import type { Session } from "@supabase/supabase-js";

interface AppState {
  session: Session | null;
  profile: Profile | null;
  authReady: boolean;
  setSession: (s: Session | null) => void;
  setProfile: (p: Profile | null) => void;
  setAuthReady: (v: boolean) => void;
  signOut: () => Promise<void>;
}

export const useAppStore = create<AppState>((set) => ({
  session: null,
  profile: null,
  authReady: false,
  setSession: (s) => set({ session: s }),
  setProfile: (p) => set({ profile: p }),
  setAuthReady: (v) => set({ authReady: v }),
  signOut: async () => {
    await supabase.auth.signOut();
    set({ session: null, profile: null });
  },
}));

// Helpers
export const fetchProfile = async (userId: string): Promise<Profile | null> => {
  const { data, error } = await supabase
    .from("profiles")
    .select(
      "user_id, email, company, business_name, logo_url, experience_years, gstin, gst_status, role, verified, phone, phone_verified, full_name, pan, aadhaar_last4, service_locations, service_categories, min_tonnage, max_tonnage, profile_completion, profile_complete, bank_account_last4, bank_ifsc, bank_name, bank_holder_name, bank_verified, bank_verified_at, accepted_tc, tc_accepted_date, subscription_tier, trial_start_date, posts_used, trial_status",
    )
    .eq("user_id", userId)
    .maybeSingle();
  if (error || !data) return null;
  const d = data as any;
  return {
    userId: d.user_id,
    email: d.email,
    company: d.company,
    businessName: d.business_name ?? "",
    fullName: d.full_name ?? "",
    logoUrl: d.logo_url ?? null,
    experienceYears: Number(d.experience_years ?? 0),
    gstin: d.gstin,
    gstStatus: (d.gst_status ?? "Unregistered") as
      | "Registered"
      | "Unregistered"
      | "Pending",
    role: d.role as "buyer" | "seller",
    verified: d.verified,
    phone: d.phone ?? "",
    phoneVerified: Boolean(d.phone_verified),
    pan: d.pan ?? "",
    aadhaarLast4: d.aadhaar_last4 ?? "",
    serviceLocations: (d.service_locations ?? []) as string[],
    serviceCategories: (d.service_categories ?? []) as NonNullable<Profile["serviceCategories"]>,
    minTonnage: Number(d.min_tonnage ?? 0),
    maxTonnage: Number(d.max_tonnage ?? 0),
    profileCompletion: Number(d.profile_completion ?? 0),
    profileComplete: Boolean(d.profile_complete),
    bankAccountLast4: d.bank_account_last4 ?? "",
    bankIfsc: d.bank_ifsc ?? "",
    bankName: d.bank_name ?? "",
    bankHolderName: d.bank_holder_name ?? "",
    bankVerified: Boolean(d.bank_verified),
    bankVerifiedAt: d.bank_verified_at ?? null,
    acceptedTc: Boolean(d.accepted_tc),
    tcAcceptedDate: d.tc_accepted_date ?? null,
    subscriptionTier: (d.subscription_tier ?? "none") as NonNullable<Profile["subscriptionTier"]>,
    trialStartDate: d.trial_start_date ?? null,
    postsUsed: Number(d.posts_used ?? 0),
    trialStatus: (d.trial_status ?? "inactive") as NonNullable<Profile["trialStatus"]>,
  };
};
